import React, { forwardRef } from 'react';
import cx from 'classnames';

import { Icon } from '../Icon/Icon';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

import * as cs from './Input.module.css';

interface Props
  extends React.HTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  as: 'input' | 'textarea';
  label: string;
  id: string;
  name: string;
  disabled?: boolean;
  error?: string | null;
  value?: string;
  autocomplete?: string;
  rows?: number;
  type?: string;
  wrapperClassName?: string;
  hiddenLabel?: boolean;
  light?: boolean;
  placeholder?: string;
}

const Input = forwardRef(
  (
    {
      as: ComponentToRender,
      label,
      id,
      name,
      disabled = false,
      error = null,
      value,
      autocomplete,
      rows,
      className,
      type,
      wrapperClassName,
      hiddenLabel,
      light,
      ...restProps
    }: Props,
    ref: React.ForwardedRef<any>,
  ) => {
    const describedById = error ? `${id}-error` : '';

    return (
      <label
        className={cx(
          cs.root,
          {
            [cs.error]: Boolean(error),
          },
          wrapperClassName,
        )}
        htmlFor={id}
      >
        {hiddenLabel && <VisuallyHidden>{label}</VisuallyHidden>}
        <ComponentToRender
          className={cx(cs.field, { [cs.light]: light }, className)}
          id={id}
          ref={ref}
          name={name}
          disabled={disabled}
          aria-describedby={describedById}
          value={value}
          autoComplete={autocomplete}
          rows={rows}
          type={type}
          {...restProps}
        />
        {!hiddenLabel && <span className={cs.label}>{label}</span>}
        {Boolean(error) && (
          <>
            <Icon className={cs.icon} name="warning" size={20} />
            <div id={describedById} className={cs.errorMessage}>
              {error}
            </div>
          </>
        )}
      </label>
    );
  },
);

Input.displayName = 'Input';

export { Input };
